import { NgClass } from '@angular/common';
import { Component, DestroyRef, inject, input, output, Renderer2 } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router, RouterModule } from '@angular/router';
import { MenuItem } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { LanguageOption } from '../../models/layout.models';
import { LayoutService } from '../../services/layout.service';
import { SidebarComponent } from '../sidebar/sidebar.component';
import { TopbarComponent } from '../topbar/topbar.component';

@Component({
  selector: 'igx-layout',
  imports: [TopbarComponent, RouterModule, NgClass, ToastModule, SidebarComponent],
  templateUrl: './layout.component.html',
  styleUrl: './layout.component.scss',
})
export class LayoutComponent {
  public menu = input.required<MenuItem[]>();
  public router = inject(Router);
  public hideSideBar = input<boolean>(false);
  public languages = input<LanguageOption[]>([]);
  public currentLanguage = input<string>();
  public languageChange = output<string>();

  private readonly _destroyRef = inject(DestroyRef);
  private readonly _layoutService = inject(LayoutService);
  private readonly renderer = inject(Renderer2);

  public menuOutsideClickListener: () => void | null;

  public isOutsideClicked(event: MouseEvent) {
    const sidebarEl = document.querySelector('.sidebar');
    const topbarEl = document.querySelector('.topbar__hamburger');
    const eventTarget = event.target as Node;

    return !(
      sidebarEl?.isSameNode(eventTarget) ||
      sidebarEl?.contains(eventTarget) ||
      topbarEl?.isSameNode(eventTarget) ||
      topbarEl?.contains(eventTarget)
    );
  }

  public readonly overlayMenuOpenSubscription = this._layoutService.overlayOpen$
    .pipe(takeUntilDestroyed(this._destroyRef))
    .subscribe(() => {
      if (!this.menuOutsideClickListener) {
        this.menuOutsideClickListener = this.renderer.listen('document', 'click', (event) => {
          if (this.isOutsideClicked(event)) {
            this.hideMenu();
          }
        });
      }

      if (this._layoutService.layoutState().staticMenuMobileActive) {
        this.blockBodyScroll();
      }
    });

  public hideMenu() {
    this._layoutService.layoutState.update((prev) => ({
      ...prev,
      overlayMenuActive: false,
      staticMenuMobileActive: false,
      menuHoverActive: false,
    }));
    if (this.menuOutsideClickListener) {
      this.menuOutsideClickListener();
      this.menuOutsideClickListener = null;
    }
    this.unblockBodyScroll();
  }

  public blockBodyScroll(): void {
    if (document.body.classList) {
      document.body.classList.add('blocked-scroll');
    } else {
      document.body.className += ' blocked-scroll';
    }
  }

  public unblockBodyScroll(): void {
    if (document.body.classList) {
      document.body.classList.remove('blocked-scroll');
    } else {
      document.body.className = document.body.className.replace(
        new RegExp('(^|\\b)' + 'blocked-scroll'.split(' ').join('|') + '(\\b|$)', 'gi'),
        ' '
      );
    }
  }

  public get containerClass() {
    return {
      'layout--overlay': this._layoutService.layoutConfig().menuMode === 'overlay',
      'layout--static': this._layoutService.layoutConfig().menuMode === 'static',
      'layout--static-inactive':
        this._layoutService.layoutState().staticMenuDesktopInactive &&
        this._layoutService.layoutConfig().menuMode === 'static',
      'layout--overlay-active': this._layoutService.layoutState().overlayMenuActive,
      'layout--mobile-active': this._layoutService.layoutState().staticMenuMobileActive,
    };
  }
}
